import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { useListItem, useMergeRefs } from '@floating-ui/react';
import { FixMeLater } from '../../../utils';
import { useSelectMenuContext } from '../SelectMenu.context';
import { Styler } from '../../Styler';
import { TickIcon } from '../../../icons/TickIcon';

export type SelectMenuOptionClasses = 'root' | 'tick';

// Static styles
const STATIC_STYLES: { [key in SelectMenuOptionClasses]: SerializedStyles } = {
    root: css`
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: calc(var(--hc-spacing-unit) * 2);
        padding: calc(var(--hc-spacing-unit) * 2) calc(var(--hc-spacing-unit) * 2);
        font-size: 0.875rem;

        &[data-active='true'] {
            background-color: var(--hc-color-neutral-tint-100);
        }

        &[aria-selected='true'] {
            > [data-tick] {
                visibility: visible;
            }
        }
    `,
    tick: css`
        color: var(--hc-color-primary-base);
        width: 16px;
        height: 16px;
        visibility: hidden;
    `,
};

export type SelectMenuOptionProps = {
    /**
     * The label which will be displayed on the option.
     */
    children: string;

    /**
     * A unique value for the option.
     */
    value: string;
};

export const SelectMenuOption: React.ForwardRefExoticComponent<
    SelectMenuOptionProps & React.RefAttributes<HTMLElement>
> = React.forwardRef(function SelectMenuOption(
    props: SelectMenuOptionProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const { children: label, value, ...other } = props;

    const { activeIndex, selectedOption, getItemProps, handleSelect } = useSelectMenuContext();

    const { ref: listItemRef, index } = useListItem({ label });

    const handleRef = useMergeRefs([ref, listItemRef]);

    const active = index === activeIndex;
    const selected = index === selectedOption?.index;

    return (
        <Styler
            ref={handleRef}
            button={true}
            baseStyles={STATIC_STYLES.root}
            role="option"
            aria-selected={selected}
            data-active={active}
            // Make the element focusable using a roving tabIndex.
            tabIndex={active ? 0 : -1}
            {...getItemProps({
                onClick: () => handleSelect(index),
            })}
            data-value={value}
            {...other}
        >
            <TickIcon css={STATIC_STYLES.tick} data-tick={true} />
            {label}
        </Styler>
    );
});
